"use client";

import { useEffect } from "react";

/* Registers the worker, and then does the part that was missing: asks whether there is a
   new one every time the app comes back to the front.

   An installed app is not a browser tab. Somebody opens it from the home screen, uses it,
   and switches away, and the next time they come back the phone restores the page it had
   rather than navigating anywhere. The worker serves navigations network first, so a real
   navigation would always have been fresh, but a resume is not a navigation and one never
   happened. A phone could sit on a build from a week ago and nothing would ever tell it.

   So: check on mount, and check again whenever the app becomes visible. When a new worker
   takes over, reload once. The guard matters because clients.claim() fires controllerchange
   on the very first install too, and reloading a first run for no reason looks like a bug. */
export function ServiceWorker() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    /* Read once, before anything registers. The guard used to read the controller inside
       the handler, where it is always the new worker and never null, so it never guarded
       anything: every first visit loaded the page, installed the worker, and loaded the
       whole page again. Lighthouse counted that as a redirect costing 2 to 4 seconds on a
       phone, on the one visit that decides whether somebody stays. */
    let controlled = Boolean(navigator.serviceWorker.controller);
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      // Nothing was controlling this page before, so this is the first install rather than
      // an update. There is nothing stale to reload away from.
      if (!controlled) {
        controlled = true;
        return;
      }
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });

    let registration: ServiceWorkerRegistration | undefined;

    navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .then((reg) => {
        registration = reg;
        return reg.update();
      })
      .catch(() => {});

    const check = () => {
      if (document.visibilityState === "visible") registration?.update().catch(() => {});
    };

    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, []);

  return null;
}
